/**
 * What a round is called — the browser's copy of the same rule the scheduling
 * function applies (supabase/functions/_shared/round-name.ts). A round's name
 * is the name its games carry in `scheduled_games.bracket_label`; where they
 * disagree, the label most games carry wins and a tie goes to the lowest game
 * number. There is no rounds table.
 */

export type LabelledGame = {
  bracket_label?: string | null;
  game_number?: number | null;
};

const BYE = / \(bye\)$/i;
/** "Round 5 #1" is what the machine called a game nobody has named yet — it is
 *  an absence of a name, not a name, so it never speaks for its round. */
const AUTO = /^round \d+ #\d+$/i;

export function stripBye(label: string): string {
  return String(label ?? '').replace(BYE, '').trim();
}

export function isByeLabel(label?: string | null): boolean {
  return BYE.test(String(label ?? ''));
}

export function roundNameOf(games: LabelledGame[]): string | null {
  const counts = new Map<string, { n: number; first: number }>();
  for (const g of games ?? []) {
    const label = stripBye(String(g?.bracket_label ?? ''));
    if (!label || AUTO.test(label)) continue;
    const cur = counts.get(label) ?? { n: 0, first: Number.POSITIVE_INFINITY };
    cur.n += 1;
    cur.first = Math.min(cur.first, Number(g?.game_number ?? Number.POSITIVE_INFINITY));
    counts.set(label, cur);
  }
  let best: string | null = null;
  let bestVal = { n: -1, first: Number.POSITIVE_INFINITY };
  for (const [label, v] of counts) {
    if (v.n > bestVal.n || (v.n === bestVal.n && v.first < bestVal.first)) {
      best = label;
      bestVal = v;
    }
  }
  return best;
}

export function fallbackRoundName(round: number): string {
  return `Round ${round}`;
}

export function displayRoundName(games: LabelledGame[], round: number): string {
  return roundNameOf(games) || fallbackRoundName(round);
}

/** Which games a rename touches, and which keep the name they were given. */
export function planRoundRename(
  games: Array<LabelledGame & { id: string }>,
  round: number,
  newName: string,
) {
  const current = displayRoundName(games, round);
  const next = String(newName ?? '').trim() || fallbackRoundName(round);
  const renaming: Array<{ id: string; label: string }> = [];
  const keeping: Array<{ id: string; label: string }> = [];
  for (const g of games ?? []) {
    const raw = String(g?.bracket_label ?? '').trim();
    const bare = stripBye(raw);
    if (!bare || AUTO.test(bare) || bare.toLowerCase() === current.toLowerCase()) renaming.push({ id: (g as any).id, label: raw || current });
    else keeping.push({ id: (g as any).id, label: raw });
  }
  return { current, next, renaming, keeping };
}

/** "Rename Round 4 to Championship. 1 game takes the new name; 3rd Place keeps its own." */
export function renamePlanSentence(plan: ReturnType<typeof planRoundRename>): string {
  const n = plan.renaming.length;
  const head = `Rename ${plan.current} to ${plan.next}. ${n} game${n === 1 ? '' : 's'} take${n === 1 ? 's' : ''} the new name`;
  if (!plan.keeping.length) return `${head}.`;
  const kept = Array.from(new Set(plan.keeping.map((k) => k.label)));
  const list = kept.length <= 3 ? kept.join(', ') : `${kept.slice(0, 3).join(', ')} and ${kept.length - 3} more`;
  return `${head}; ${list} keep${kept.length === 1 ? 's' : ''} ${kept.length === 1 ? 'its' : 'their'} own.`;
}
